/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Theme, darken, lighten, makeStyles } from '@material-ui/core/styles';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronCircleDown } from '@fortawesome/pro-duotone-svg-icons/faChevronCircleDown';

import { faChevronCircleUp } from '@fortawesome/pro-duotone-svg-icons/faChevronCircleUp';

import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';

import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons/faExclamationCircle';

import { faTimesCircle } from '@fortawesome/pro-duotone-svg-icons/faTimesCircle';




import { useField, useFormState } from 'react-final-form';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */



import * as Dtos from '../../api/Neon.Dtos';

import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import FieldContext from '../../context/FieldContext';
import FieldErrorContext from '../../context/FieldErrorContext';
import FormGridRowErrorContext from '../../context/FormGridErrorContext';
import StaticRowErrorContext from '../../context/StaticRowErrorContext';
import { pascalToCamelCase } from './OpmsInput';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
interface IOpmsFieldErrorFlags {
    name?: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => {
    const getColor = theme.palette.type === 'light' ? darken : lighten;
    const getBackgroundColor = theme.palette.type === 'light' ? lighten : darken;
    const getBorderColor = theme.palette.type === 'light' ? lighten : darken;

    return {
        icon: {
            padding: theme.spacing(0, 1),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderWidth: 1,
            borderStyle: 'solid',
            '& > *': {
                opacity: 0.9
            },
            '& $hover': {
                display: 'none'
            },
            '&:hover': {
                '& $hover': {
                    display: 'block'
                },
                '& $default': {
                    display: 'none'
                },
            }
        },
        loading: {
            color: getColor(theme.palette.grey[700], 0.6),
            backgroundColor: getBackgroundColor(theme.palette.grey[700], 0.9),
            borderColor: getBorderColor(theme.palette.grey[700], 0.8),
            '& > *': {
                color: theme.palette.grey[700],
            }
        },
        notes: {
            color: getColor(theme.palette.info.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),
            borderColor: getBorderColor(theme.palette.info.main, 0.8),
            '& > *': {
                color: theme.palette.info.main,
            }
        },
        eligibilities: {
            color: getColor(theme.palette.warning.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.warning.main, 0.9),
            borderColor: getBorderColor(theme.palette.warning.main, 0.8),
            '& > *': {
                color: theme.palette.warning.main
            }
        },
        errors: {
            color: getColor(theme.palette.error.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9),
            borderColor: getBorderColor(theme.palette.error.main, 0.8),
            '& > *': {
                color: theme.palette.error.main,
            }
        },
        stratificationFailures: {
            color: '#fff',
            fontWeight: theme.typography.fontWeightMedium,
            backgroundColor: theme.palette.warning.main,
            borderColor: getColor(theme.palette.warning.main, 0.1),
        },
        criticals: {
            color: '#fff',
            fontWeight: theme.typography.fontWeightMedium,
            backgroundColor: theme.palette.error.main,
            borderColor: getColor(theme.palette.error.main, 0.1),
        },
        default: {
        },
        hover: {
        }
    }
});


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const OpmsFieldErrorFlags: React.FunctionComponent<IOpmsFieldErrorFlags> = ({
    name
}) => {
    const classes = useStyles();

    const fieldName = React.useContext(FieldContext);

    const fieldName2 = React.useMemo(() => {
        if (name) {
            return name;
        }

        if (!fieldName) {
            return '';
        }

        const splitFieldConst = fieldName.split('.');

        return splitFieldConst.map(s => pascalToCamelCase(s)).join('.');

    }, [fieldName, name]);

    const { meta: { active, touched } } = useField(fieldName2, { subscription: { active: true, touched: true } });

    const { input: { value: formId } } = useField('id', { subscription: { value: true } });

    const fieldErrorState = React.useContext(FieldErrorContext);

    const staticRowErrorState = React.useContext(StaticRowErrorContext);

    const formGridRowErrorState = React.useContext(FormGridRowErrorContext);

    const { errors: formErrors, validating } = useFormState({ subscription: { errors: true, validating: true } });

    const typedErrors: Dtos.IValidationError[] | undefined = React.useMemo(() => {
        if (!formErrors || !formErrors[name ?? fieldName]) {
            return [];
        }

        return formErrors[name ?? fieldName];
    }, [fieldName, name, formErrors]);

    // const typedErrors: Dtos.IOpmsValidationError[] | undefined = error;

    const notes = typedErrors?.some(e => e.type === Dtos.ErrorType.Warning);

    const eligibilities = typedErrors?.some(e => e.type === Dtos.ErrorType.Ineligible);

    const errors = typedErrors?.some(e => e.type === Dtos.ErrorType.Normal);

    const stratifications = typedErrors?.some(e => e.type === Dtos.ErrorType.StratificationFailure);

    const criticals = typedErrors?.some(e => e.type === Dtos.ErrorType.Critical);

    const onClick = React.useCallback(() => {
        if (formGridRowErrorState) {
            formGridRowErrorState.setExpanded(!formGridRowErrorState.expanded)
        }
        else if (staticRowErrorState) {
            staticRowErrorState.setExpanded(!staticRowErrorState.expanded)
        }
        else if (fieldErrorState) {
            fieldErrorState.setExpanded(!fieldErrorState.expanded)
        }
    }, [fieldErrorState, staticRowErrorState, formGridRowErrorState])

    if (!formId && !touched) {
        return null;
    }

    if (validating && (active || notes || eligibilities || errors || stratifications || criticals)) {
        return (
            <ButtonBase
                className={`${classes.icon} ${classes.loading}`}
                onClick={onClick}
            >
                <div>
                    <CircularProgress size={16} />
                </div>
            </ButtonBase>
        );
    }

    return (
        <>
            {
                notes && !eligibilities && !errors && !stratifications && !criticals && (
                    <ButtonBase
                        className={`${classes.icon} ${classes.notes}`}
                        onClick={onClick}
                    >
                        <FontAwesomeIcon className={classes.default} icon={faInfoCircle} fixedWidth />
                        <FontAwesomeIcon className={classes.hover} icon={fieldErrorState?.expanded ? faChevronCircleUp : faChevronCircleDown} fixedWidth />
                    </ButtonBase>
                )
            }
            {
                eligibilities && !errors && !stratifications && !criticals && (
                    <ButtonBase
                        className={`${classes.icon} ${classes.eligibilities}`}
                        onClick={onClick}
                    >
                        <FontAwesomeIcon className={classes.default} icon={faExclamationCircle} fixedWidth />
                        <FontAwesomeIcon className={classes.hover} icon={fieldErrorState?.expanded ? faChevronCircleUp : faChevronCircleDown} fixedWidth />
                    </ButtonBase>
                )
            }
            {
                errors && !stratifications && !criticals && (
                    <ButtonBase
                        className={`${classes.icon} ${classes.errors}`}
                        onClick={onClick}
                    >
                        <FontAwesomeIcon className={classes.default} icon={faTimesCircle} fixedWidth />
                        <FontAwesomeIcon className={classes.hover} icon={fieldErrorState?.expanded ? faChevronCircleUp : faChevronCircleDown} fixedWidth />
                    </ButtonBase>
                )
            }
            {
                stratifications && !criticals && (
                    <ButtonBase
                        className={`${classes.icon} ${classes.stratificationFailures}`}
                        onClick={onClick}
                    >
                        <FontAwesomeIcon className={classes.default} icon={faExclamationCircle} fixedWidth />
                        <FontAwesomeIcon className={classes.hover} icon={fieldErrorState?.expanded ? faChevronCircleUp : faChevronCircleDown} fixedWidth />
                    </ButtonBase>
                )
            }
            {
                criticals && (
                    <ButtonBase
                        className={`${classes.icon} ${classes.criticals}`}
                        onClick={onClick}
                    >
                        <FontAwesomeIcon className={classes.default} icon={faTimesCircle} fixedWidth />
                        <FontAwesomeIcon className={classes.hover} icon={fieldErrorState?.expanded ? faChevronCircleUp : faChevronCircleDown} fixedWidth />
                    </ButtonBase>
                )
            }
        </>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsFieldErrorFlags;
